import React, { useState, useMemo } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { DatePicker } from "@/components/ui/date-picker";
import { Button } from "@/components/ui/button";
import { SearchIcon } from "lucide-react";
import { toast } from '@/components/ui/toast';
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { calculateUnits } from "./calculateUnits";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableFooter,
} from "@/components/ui/table";
import {
  PieChart,
  Pie,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";

export const GET_CALAIM_NOTES = gql`
  query GetCalaimNotes($startDate: Date, $endDate: Date) {
    calaimNotes(startDate: $startDate, endDate: $endDate) {
      id
      procedurecode
      proceduremodifier
      procedurelength
    }
  }
`;

const procedureDescriptions = {
  "G9008 U1": "G9008 U1 In Person, Clinical Staff $31.68",
  "G9008 U1 GQ": "G9008 U1 GQ Phone/Telehealth, Clinical Staff $21.12",
  "G9012 U2": "G9012 U2 In Person, Non Clinical Staff $17.07",
  "G9012 U2 GQ": "G9012 U2 GQ Phone/Telehealth, Non Clinical Staff $11.38",
  "G9008 U8": "G9008 U8 Outreach, In Person, Clinical Staff $130.46",
  "G9008 U8 GQ": "G9008 U8 GQ Outreach, Phone/Telehealth, Clinical Staff $86.97 ",
  "G9012 U8": "G9012 U8 Outreach, In Person, Non Clinical Staff $70.69",
  "G9012 U8 GQ": "G9012 U8 GQ Outreach, Phone/Telehealth, Non Clinical Staff $47.13",
  "G9007": "G9007",
} as any;

const procedurePrices: Record<string, number> = {
  "G9008 U1": 31.68,
  "G9008 U1 GQ": 21.12,
  "G9012 U2": 17.07,
  "G9012 U2 GQ": 11.38,
  "G9008 U8": 130.46,
  "G9008 U8 GQ": 86.97,
  "G9012 U8": 70.69,
  "G9012 U8 GQ": 47.13,
  "G9007": 0,
};

const COLORS = [
  '#8884d8', '#82ca9d', '#ffc658', '#d0ed57',
  '#a4de6c', '#8dd1e1', '#83a6ed', '#8a8aff',
];

const BUCKET_MINUTES = [
  15,
  30,
  45,
  60,
  75,
  90,
  105,
  120,
  135,
  150,
  165,
  180,
  195,
  210,
  225,
  240,
] as const;

type Bucket = `${typeof BUCKET_MINUTES[number]}min`;

const buckets = BUCKET_MINUTES.map((minutes) => `${minutes}min` as Bucket);
const bucketMinutes = BUCKET_MINUTES.reduce((map, minutes) => {
  map[`${minutes}min` as Bucket] = minutes;
  return map;
}, {} as Record<Bucket, number>);

function normalizeKey(key: string): string {
  return key.trim().replace(/\s+/g, ' ').toUpperCase();
}

function getProcedureKey(note: any): string {
  const code = note.procedurecode.trim().toUpperCase();
  const mod = note.proceduremodifier?.trim().toUpperCase() || '';
  return normalizeKey(mod ? `${code} ${mod}` : code);
}

function getBucketForLength(length: number): Bucket {
  const unitMinutes = calculateUnits(length) * 15;
  const cappedMinutes = Math.min(Math.max(unitMinutes, 15), 240);
  return `${cappedMinutes}min` as Bucket;
}

export const FeeForServiceReport: React.FC = () => {
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [fetchNotes, { data, error }] = useLazyQuery(GET_CALAIM_NOTES);

  const notes = useMemo(() => data?.calaimNotes ?? [], [data]);

  const {
    distribution, countTotals, bucketAmounts,
    grandTotalCount, grandTotalAmount,
  } = useMemo(() => {
    const dist: Record<string, Record<string, number>> = {};
    const counts: Record<string, number> = {};
    const amts: Record<string, number> = {};
    let totalCount = 0;
    let totalAmount = 0;

    buckets.forEach((bucket) => {
      counts[bucket] = 0;
      amts[bucket] = 0;
    });

    notes.forEach((note: any) => {
      const procedureKey = getProcedureKey(note);
      dist[procedureKey] ||= buckets.reduce((row, bucket) => {
        row[bucket] = 0;
        return row;
      }, {} as Record<string, number>);

      const length = Number(note.procedurelength || 0);
      const bucket = getBucketForLength(length);
      dist[procedureKey][bucket] += 1;
    });

    Object.entries(dist).forEach(([procedureKey, bucketCounts]) => {
      let rowTotal = 0;

      buckets.forEach((bucket) => {
        const quantity = bucketCounts[bucket] || 0;
        rowTotal += quantity;
        counts[bucket] += quantity;

        const price = procedurePrices[procedureKey] || 0;
        if (!price || !quantity) {
          return;
        }

        const units = calculateUnits(bucketMinutes[bucket]);
        const amount = units * price * quantity;
        amts[bucket] = (amts[bucket] || 0) + amount;
        totalAmount += amount;
      });

      totalCount += rowTotal;
    });

    return {
      distribution: dist,
      countTotals: counts,
      bucketAmounts: amts,
      grandTotalCount: totalCount,
      grandTotalAmount: totalAmount,
    };
  }, [notes]);

  const sortedKeys = useMemo(
    () => Object.keys(distribution).sort((a, b) =>
      a === 'G9007' ? -1 : b === 'G9007' ? 1 : a.localeCompare(b)
    ),
    [distribution]
  );
  const fifteenMinuteCases = countTotals['15min'] || 0;
  const allOtherCases = grandTotalCount - fifteenMinuteCases;
  const pct = (count: number) =>
    grandTotalCount ? ((count / grandTotalCount) * 100).toFixed(1) + '%' : '0%';

  const timeData = buckets.map((bucket) => ({ name: bucket, value: countTotals[bucket] || 0 }));
  const procData = sortedKeys.map((key) => ({
    name: procedureDescriptions[key] || key,
    value: Object.values(distribution[key] || {}).reduce((sum, count) => sum + (count || 0), 0),
  }));

  const search = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both dates');
      return;
    }

    fetchNotes({ variables: { startDate, endDate } });
  };

  const downloadExcel = () => {
    const wsData: any[][] = [
      ['Description', ...buckets, 'Total'],
      ...sortedKeys.map((key) => [
        procedureDescriptions[key] || key,
        ...buckets.map((bucket) => distribution[key]?.[bucket] || 0),
        buckets.reduce((sum, bucket) => sum + (distribution[key]?.[bucket] || 0), 0),
      ]),
      ['Total Cases', ...buckets.map((bucket) => countTotals[bucket] || 0), grandTotalCount],
      ['Amount Billed', ...buckets.map((bucket) => (bucketAmounts[bucket] || 0).toFixed(2)), grandTotalAmount.toFixed(2)],
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, 'ProcedureReport.xlsx');
  };

  return (
    <div className="space-y-4 text-xs">
      <div className="flex gap-4 items-end">
        <DatePicker date={startDate} setDate={setStartDate} label="Start Date" />
        <DatePicker date={endDate} setDate={setEndDate} label="End Date" />
        <Button onClick={search} className="flex items-center gap-2">
          <SearchIcon size={16} /><span>Search</span>
        </Button>
        <Button onClick={downloadExcel} className="flex items-center gap-2">
          <span>Download Excel</span>
        </Button>
      </div>

      {error && <div className="text-red-600">Error: {error.message}</div>}

      {!data?.calaimNotes ? (
        <div className="p-4 text-center text-muted-foreground">
          Set date range and click Search
        </div>
      ) : (
        <>
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                {buckets.map((bucket) => <TableHead key={bucket}>{bucket}</TableHead>)}
                <TableHead>Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedKeys.map((key) => (
                <TableRow key={key}>
                  <TableCell>{procedureDescriptions[key] || key}</TableCell>
                  {buckets.map((bucket) => <TableCell key={bucket}>{distribution[key]?.[bucket] || 0}</TableCell>)}
                  <TableCell>
                    {buckets.reduce((sum, bucket) => sum + (distribution[key]?.[bucket] || 0), 0)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow className="font-semibold">
                <TableCell>Total Cases</TableCell>
                {buckets.map((bucket) => <TableCell key={bucket}>{countTotals[bucket] || 0}</TableCell>)}
                <TableCell>{grandTotalCount || 0}</TableCell>
              </TableRow>
              <TableRow className="font-semibold">
                <TableCell>Amount Billed</TableCell>
                {buckets.map((bucket) =>
                  <TableCell key={bucket}>${(bucketAmounts[bucket] || 0).toFixed(2)}</TableCell>
                )}
                <TableCell>${(grandTotalAmount || 0).toFixed(2)}</TableCell>
              </TableRow>
            </TableFooter>
          </Table>

          <div className="mt-4">
            <p>15-min cases: {fifteenMinuteCases} ({pct(fifteenMinuteCases)})</p>
            <p>All other cases: {allOtherCases} ({pct(allOtherCases)})</p>
          </div>

          <div className="flex flex-col md:flex-row gap-8 mt-4">
            <div className="flex-1">
              <h3 className="font-semibold mb-2">Time Distribution</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={timeData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ value }) => pct(value)}
                  >
                    {timeData.map((_, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold mb-2">Procedure Breakdown</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={procData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {procData.map((_, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
